/**
 * Generic OIDC provider wrapper.
 *
 * Wraps react-oidc-context configuration to be provider-agnostic.
 */

import type { OidcProviderConfig, AuthProviderType, SignoutRedirectArgs } from './types';

/**
 * Get the current auth provider from environment.
 */
export function getAuthProvider(): AuthProviderType {
  const provider = import.meta.env.VITE_AUTH_PROVIDER || 'cognito';
  return provider as AuthProviderType;
}

/**
 * Build OIDC configuration for the current environment.
 *
 * Handles the different configuration patterns:
 * - Local mode: Mock auth (no OIDC)
 * - Deployed mode: VITE_AUTHORITY is set
 * - Dev mode: VITE_ENVIRONMENT=dev with VITE_DEV_COGNITO_* vars
 *
 * @returns OIDC configuration or null for local/mock mode
 */
export function buildOidcConfig(): OidcProviderConfig | null {
  const environment = import.meta.env.VITE_ENVIRONMENT || 'local';

  // Check for deployed configuration (Terraform-injected)
  if (import.meta.env.VITE_AUTHORITY) {
    return {
      authority: import.meta.env.VITE_AUTHORITY,
      clientId: import.meta.env.VITE_OIDC_CLIENT_ID || '',
      redirectUri: import.meta.env.VITE_OIDC_REDIRECT_URI || window.location.origin,
      postLogoutRedirectUri:
        import.meta.env.VITE_OIDC_POST_LOGOUT_REDIRECT_URI || window.location.origin,
      scope: import.meta.env.VITE_OIDC_SCOPE || 'openid email profile',
      responseType: 'code',
    };
  }

  // Local/mock mode - no OIDC needed
  if (environment === 'local') {
    return null;
  }

  // Dev mode - use VITE_DEV_COGNITO_* vars
  if (environment === 'dev') {
    return {
      authority: import.meta.env.VITE_DEV_COGNITO_AUTHORITY || '',
      clientId: import.meta.env.VITE_DEV_COGNITO_CLIENT_ID || '',
      redirectUri: window.location.origin,
      postLogoutRedirectUri: window.location.origin,
      scope: 'openid email profile aws.cognito.signin.user.admin',
      responseType: 'code',
    };
  }

  // Prod mode - use VITE_PROD_COGNITO_* vars
  if (environment === 'prod') {
    return {
      authority: import.meta.env.VITE_PROD_COGNITO_AUTHORITY || '',
      clientId: import.meta.env.VITE_PROD_COGNITO_CLIENT_ID || '',
      redirectUri: window.location.origin,
      postLogoutRedirectUri: window.location.origin,
      scope: 'openid email profile',
      responseType: 'code',
    };
  }

  return null;
}

/**
 * Check if mock/local authentication should be used.
 */
export function useMockAuth(): boolean {
  const environment = import.meta.env.VITE_ENVIRONMENT || 'local';
  return environment === 'local' && !import.meta.env.VITE_AUTHORITY;
}

/**
 * Get the OIDC scope based on provider.
 *
 * Different providers have different scope requirements.
 */
export function getProviderScope(provider: AuthProviderType): string {
  switch (provider) {
    case 'cognito':
      return 'openid email profile aws.cognito.signin.user.admin';
    case 'entra':
      return 'openid email profile';
    default:
      return 'openid email profile';
  }
}

/**
 * Build provider-specific logout arguments.
 *
 * Different OIDC providers have different logout URL requirements:
 * - AWS Cognito: Requires client_id and logout_uri query parameters
 * - Entra ID: Follows standard OIDC with id_token_hint
 * - Standard OIDC: Uses id_token_hint and post_logout_redirect_uri
 *
 * @param provider - Auth provider type ('cognito', 'entra', 'local', 'both')
 * @param clientId - OAuth2 client ID
 * @param postLogoutRedirectUri - URL to redirect to after logout
 * @param idTokenHint - Optional ID token for session identification
 * @returns SignoutRedirectArgs configured for the specific provider
 */
export function buildLogoutArgs(
  provider: AuthProviderType,
  clientId: string,
  postLogoutRedirectUri: string,
  idTokenHint?: string
): SignoutRedirectArgs {
  switch (provider) {
    case 'cognito':
      // AWS Cognito requires client_id in query params
      // https://docs.aws.amazon.com/cognito/latest/developerguide/logout-endpoint.html
      return {
        extraQueryParams: {
          client_id: clientId,
          logout_uri: postLogoutRedirectUri
        }
      };
    case 'entra':
      // Microsoft Entra follows standard OIDC
      return {
        id_token_hint: idTokenHint,
        post_logout_redirect_uri: postLogoutRedirectUri
      };
    default:
      // Standard OIDC RP-Initiated Logout
      return {
        id_token_hint: idTokenHint,
        post_logout_redirect_uri: postLogoutRedirectUri
      };
  }
}
